import { z } from 'zod';
import { MANUAL_SOURCES } from './payment';

export const invoiceItemSchema = z.object({
  // Present when this item already exists on the invoice (edit flow) — lets
  // PUT /invoices/:id update it in place instead of delete+recreate, which
  // matters because BillingPeriod.invoiceItemId points at these rows (chốt-sổ
  // reopen logic keys off that link). Ignored on create.
  id: z.string().optional(),
  description: z.string().min(1, 'Description is required'),
  // Negative quantity = a deduction line (e.g. "trừ 3 buổi đã thu ngoài" for
  // tuition a teacher collected in cash before onboarding). The minus goes on
  // quantity, not unitPrice, deliberately: the chốt-sổ generated "Bù trừ" line
  // already stores a positive unitPrice with a negative quantity, and the
  // invoice already renders that (a parent sees "SL −1" on it today). Putting
  // the minus on unitPrice instead would create a second representation of
  // the same idea and force every unit-price formatter to handle negatives.
  // Zero stays invalid — it isn't a deduction, just a no-op line.
  quantity: z.number().int().refine((q) => q !== 0, 'Số lượng phải khác 0'),
  unitPrice: z.number().min(0),
});

export const createInvoiceSchema = z.object({
  clientId: z.string(),
  items: z.array(invoiceItemSchema).min(1, 'At least one item is required'),
  taxRate: z.number().min(0).max(100).default(0),
  dueDate: z.string().datetime().nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
  sepayTxnId: z.string().nullable().optional(),
  artifactData: z.unknown().nullable().optional(),
  chatThreadId: z.string().nullable().optional(),
  artifactIndex: z.number().int().nonnegative().nullable().optional(),
});

export const updateInvoiceSchema = createInvoiceSchema.partial().extend({
  // On update the client sends ONLY manual items; chốt-sổ (generated) items are
  // preserved server-side. An invoice with no manual items → an empty array is
  // valid (unlike create's min(1)).
  items: z.array(invoiceItemSchema).optional(),
});

export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;

export const sendInvoiceEmailSchema = z.object({
  to: z.string().email(),
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(5000),
});

export type SendInvoiceEmailInput = z.infer<typeof sendInvoiceEmailSchema>;

export const markSharedSchema = z.object({
  channel: z.enum(['zalo', 'sms', 'other']).default('zalo'),
});

export type MarkSharedInput = z.infer<typeof markSharedSchema>;

// ── Bulk invoice actions (selection bar on the invoices list) ───────────────
// Capped well above a realistic monthly chốt-sổ batch, but bounded so one
// bulk call stays a single, boundedly-sized $transaction.
export const MAX_BULK_INVOICE_IDS = 100;

const bulkInvoiceIdsSchema = z
  .array(z.string().min(1))
  .min(1, 'Chọn ít nhất một hoá đơn')
  .max(MAX_BULK_INVOICE_IDS, `Chỉ chọn tối đa ${MAX_BULK_INVOICE_IDS} hoá đơn mỗi lần`);

// POST /invoices/bulk/mark-paid — one payment source for the whole batch (the
// teacher's real case: "these N parents all transferred"), defaulting is a FE
// concern (RecordPaymentDrawer/confirm dialog); the API just validates it's a
// manual source, same set POST /payments accepts.
export const bulkMarkPaidInvoicesSchema = z.object({
  invoiceIds: bulkInvoiceIdsSchema,
  source: z.enum(MANUAL_SOURCES),
});

export type BulkMarkPaidInvoicesInput = z.infer<typeof bulkMarkPaidInvoicesSchema>;

// POST /invoices/bulk/delete
export const bulkDeleteInvoicesSchema = z.object({
  invoiceIds: bulkInvoiceIdsSchema,
});

export type BulkDeleteInvoicesInput = z.infer<typeof bulkDeleteInvoicesSchema>;

// POST /invoices/bulk/due-date (THI-100) — DRAFT-only re-date. `dueDate` is a
// plain `YYYY-MM-DD`; the today-or-future check needs `nowWallClock()`
// (@repo/shared-types), which this package doesn't depend on, so — same as
// invoicingRunSchema's dueDate — that check runs in the route handler.
export const bulkDueDateInvoicesSchema = z.object({
  invoiceIds: bulkInvoiceIdsSchema,
  dueDate: z.string().date('dueDate must be YYYY-MM-DD'),
});

export type BulkDueDateInvoicesInput = z.infer<typeof bulkDueDateInvoicesSchema>;

// ── GET /invoices?from=&to= — month-window query params ─────────────────────
// Each of `from`/`to` accepts either a literal ISO date/datetime string or a
// `YYYY-MM` month shorthand (the FE's month-window helpers only ever send the
// shorthand; the literal form exists for direct/manual callers and tests).
// Both optional — absent means "no window, full fetch" (backward compatible).
const MONTH_SHORTHAND_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function isValidWindowBoundary(value: string): boolean {
  if (MONTH_SHORTHAND_RE.test(value)) return true;
  return !Number.isNaN(Date.parse(value));
}

export const invoiceListQuerySchema = z.object({
  from: z.string().refine(isValidWindowBoundary, 'from must be an ISO date or YYYY-MM').optional(),
  to: z.string().refine(isValidWindowBoundary, 'to must be an ISO date or YYYY-MM').optional(),
  /** '1' = strictly the window, no outstanding union — the FE's picked-month
   *  (archival) view. Absent/'0' = window UNION outstanding (the default view). */
  strict: z.enum(['0', '1']).optional(),
});

export type InvoiceListQueryInput = z.infer<typeof invoiceListQuerySchema>;
